import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { INewPaymentData } from "components/shared/PaymentModal";
import {
    downloadApiExport,
    getDateForFileName,
    groupObjByKey,
    multiSortWithOrder,
    sortByProperty,
} from "helpers/utils";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { Binary, IDatesRange } from "types";

import type { PaymentStatus, TParamsPagination } from "./types";

export interface DTOCreateAdvancePaymentSlice {
    amount: number | string;
    paymentDate: string | Date | null;
    status: 0 | 1 | 2;
    comment: string | null;
    id?: number;
    key?: number | string;
    paymentId?: number;
    updated?: boolean;
    sliceId?: number;
    customStatusId?: number | string | null;
}

const toCustomStatusId = (value: number | string | null | undefined): number | null => {
    if (value == null || value === "") return null;
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
};

export interface IAdvanceSlice {
    /**
     * SliceID
     */
    id: number;
    termId: number;
    companyId: number;
    idDeleted: number;
    name: string;
    author: string;
    beneficiaryId: number;
    creationDate: Date;
    title: string;
    titleId: number;
    workId?: number | null;
    totalAmount: number;
    isbn: string | null;
    /** EAN of `term.titleId` only — null on work-only terms. */
    legacyTermTitleEan13?: string | null;
    /** Real `term.titleId` — null on work-only terms. */
    legacyTermTitleId?: number | null;
    representative: string;
    titleAvatarUrl?: string;
    beneficiaryAvatarUrl?: string;
    beneficiaryNeedsResync: number; // Not sure what this is for.
    isTermDeleted: number;
    status: number;
    customStatusId?: number | null;
    amount: number;
    netToPay: number;
    paymentId: number;
    /**
     * Needed in modal for creating advancePayment.
     */
    slices?: IAdvanceSlice[];
    validBankInfos: number;
    comment: string;
    iban: string;
    bankCheck: number;
    paypalEmail: string;
    lastSentAt: Date | null;
    accountStatus: "pending" | "noaccount" | "confirmed";
    email: string;
    precount: number;
    isForfait: Binary;
    paymentDate: Date | null;
    /**
     * ID of the term_period_data connected if user created an advance payment
     * to update the N-1 balance of current period.
     */
    connectedTPD: number | null;
    /**
     * Set when the slice follows the legacy balance path (N-1 / solde). Null/undefined = droits bruts (date-based) path.
     * Mirrors `advance_payment_slice.legacyImpactBalanceN1TpdId` from the API.
     */
    legacyImpactBalanceN1TpdId?: number | null;
    /**
     * `company_period.id` for the TPD in `legacyImpactBalanceN1TpdId` (from API join).
     */
    legacyImpactBalanceN1TpdPeriodId?: number | null;
    /**
     * `term_period_data.id` when this slice is included in that row's `advanceAmount` (after recalc). From API.
     */
    impactGrossDutyTpdId?: number | null;
    /**
     * `term_period_data.periodId` for `impactGrossDutyTpdId` when set.
     */
    impactGrossDutyPeriodId?: number | null;
    /**
     * Linked to connectedTPD in order to quickly retrieve linked period.
     * If no connected TPD, it is null.
     */
    periodId: number | null;
    forceOver4Pass?: Binary;
}

export interface IAdvancePayment {
    id?: number;
    companyId: number;
    termId: number;
    beneficiaryId: number;
    representativeId: number;
    titleId: number;
    releaseDate: Date;
    ean13: string;
    title: string;
    beneficiaryName: string;
    beneficiarySurname: string;
    representative: string;
    city: string;
    country: string;
    zipCode: string;
    street: string;
    socialSecurityNumber: string;
    siretNumber: string;
    precount: boolean;
    vat: number;
    withheldPercent: number;
    creationDate: Date;
    isForfait: Binary;
    isDeleted: boolean;
    connectedTPD: number;
    totalAmount: number;
    slices: Pick<IAdvanceSlice, "id" | "status" | "paymentDate" | "amount" | "comment">[];
    originalPeriod: number | null;
}

export const fetchAllAdvanceSlices = createAsyncThunk(
    "advancePayment/fetchAllAdvanceSlices",
    async ({ options }: { options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/advance`, {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const checkIfContractAlreadyCreated = createAsyncThunk(
    "terms/checkIfContractAlreadyCreated",
    async (
        {
            titleId,
            beneficiarId,
            representativeId,
        }: { titleId: string; beneficiarId: string; representativeId: string },
        { rejectWithValue },
    ) => {
        try {
            const data = {
                titleId,
                beneficiarId,
                representativeId,
            };
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/term/check`, data);
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const sendAdvancePaymentMailWithStatement = createAsyncThunk(
    "advancePayment/sendAdvancePaymentMailWithStatement",
    async (
        {
            termId,
            advancePaymentId,
            advancePaymentSliceId,
            mailCc,
            subject,
            body,
        }: {
            termId: number;
            advancePaymentId: number;
            advancePaymentSliceId: number;
            mailCc: string[];
            subject: string;
            body: string;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/send", {
                termId,
                advancePaymentId,
                advancePaymentSliceId,
                mailCc,
                subject,
                body,
            });
            return response.data.document;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const changeAdvancePaymentStatus = createAsyncThunk(
    "advancePayment/changeAdvancePaymentStatus",
    async ({ sliceId, status }: { sliceId: number; status: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/advance/slice/${sliceId}`, {
                status: status,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Only used on term form.
 */
export const deleteAdvancePayment = createAsyncThunk(
    "advancePayment/deleteAdvancePayment",
    async ({ id, connectedTPD }: { id: number; connectedTPD?: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/advance/${id}`, {
                data: connectedTPD != null ? { connectedTPD } : {},
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteAdvanceSlice = createAsyncThunk(
    "advancePayment/deleteAdvanceSlice",
    async (
        {
            termId,
            sliceId,
            deleteWholeAdvance = false,
        }: { termId: number; sliceId: number; deleteWholeAdvance: boolean },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/advance/${termId}/slice/${sliceId}`,
                { data: { deleteWholeAdvance } },
            );
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response.data);
        }
    },
);

export const addAdvancePayment = createAsyncThunk(
    "advancePayment/addAdvancePayment",
    async (
        {
            termId,
            slices,
            isForfait,
            connectedTPD,
            periodId,
            forceOver4Pass,
        }: {
            termId: number;
            slices: DTOCreateAdvancePaymentSlice[];
            isForfait: Binary;
            periodId: number;
            connectedTPD?: number;
            forceOver4Pass: Binary;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/advance", {
                termId,
                slices: slices.map((slice) => ({ ...slice, customStatusId: toCustomStatusId(slice.customStatusId) })),
                isForfait,
                connectedTPD,
                periodId,
                forceOver4Pass,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const exportAdvancePayments = createAsyncThunk(
    "advancePayments/exportAdvancePayments",
    async (params: { startDate?: string; endDate?: string } | undefined, { rejectWithValue }) => {
        const toastId = toast.loading(`${t("Export.Pending")}`);
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/advance/export`, {
                headers: { Accept: "*/*" },
                params: params?.startDate || params?.endDate ? params : undefined,
            });
            toast.update(toastId, {
                render: `${t("Export.Successful")}`,
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });
            const fileName = `export_advance_payments_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            const isNoData = error?.response?.status === 400;
            toast.update(toastId, {
                render: isNoData ? `${t("Export.ServerError.NoData")}` : `${t("Export.Error")}`,
                type: isNoData ? "info" : "error",
                isLoading: false,
                autoClose: 5000,
            });
            return rejectWithValue(error?.response?.data);
        }
    },
);

export interface DTOUpdateAdvancePayment {
    periodId: number;
    slices: DTOCreateAdvancePaymentSlice[];
    isForfait: Binary;
    forceOver4Pass: Binary;
}

export const updateAdvancePayment = createAsyncThunk(
    "advancePayments/updateAdvancePayment",
    async ({ data, termId }: { data: DTOUpdateAdvancePayment; termId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/advance/${termId}`, {
                data: {
                    ...data,
                    slices: data.slices.map((slice) => ({
                        ...slice,
                        customStatusId: toCustomStatusId(slice.customStatusId),
                    })),
                },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getAdvanceLinkedToTPD = createAsyncThunk(
    "advancePayments/getAdvanceLinkedToTPD",
    async ({ tpdID }: { tpdID: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/advance/tpd/${tpdID}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface TermState {
    advanceSlices: IAdvanceSlice[];
    advancePaymentsStatus: "succeeded" | "idle" | "pending" | "failed";
    advancePayment: IAdvancePayment;
    allAdvancesDatesRange: IDatesRange;
}

const initialState: TermState = {
    advanceSlices: [],
    advancePaymentsStatus: "idle",
    advancePayment: {} as IAdvancePayment,
    allAdvancesDatesRange: {} as IDatesRange,
};

export const advancePaymentSlice = createSlice({
    name: "advancePayment",
    initialState,
    reducers: {
        setLastSentAtAdvance(state, action) {
            const currentPayments = JSON.parse(JSON.stringify(state.advanceSlices));
            if (currentPayments) {
                currentPayments.find((item: IAdvanceSlice) => item.id == action.payload.sliceId).lastSentAt =
                    new Date().toString();
                state.advanceSlices = currentPayments;
            }
        },
        updateAdvancePayments(state, { payload }) {
            const currentPayments = [...state.advanceSlices];
            const updatedElements: { elements: INewPaymentData[]; status: PaymentStatus; paymentDate: Date } = payload;

            updatedElements.elements.forEach((item) => {
                const currentSlice = currentPayments.find((slice: IAdvanceSlice) => slice.id === item.id);
                if (currentSlice) {
                    currentSlice.status = updatedElements.status;
                    currentSlice.paymentDate = updatedElements.paymentDate;
                }
            });

            state.advanceSlices = currentPayments;
        },
        validateAdvanceBankInfos(state, { payload }) {
            const currentPayments = [...state.advanceSlices];
            state.advanceSlices = currentPayments.map((item) => {
                if (item.beneficiaryId === payload.id) {
                    return { ...item, validBankInfos: 1 };
                } else {
                    return item;
                }
            });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchAllAdvanceSlices.fulfilled, (state, { payload }) => {
            const orderedSlices = payload.entries as IAdvanceSlice[];
            const specificSortBy = payload.extra.sortBy;

            // If they sort by anything other than slice ID we don't group by term
            // as visually it makes no sense.
            if (specificSortBy) {
                state.advanceSlices = orderedSlices;
            } else {
                // We create a map so we can keep default order and then group by termId.
                const groupedSlices: Map<number, IAdvanceSlice[][]> = new Map();

                for (const slice of orderedSlices) {
                    const termId = slice.termId;

                    if (!groupedSlices.has(termId)) {
                        groupedSlices.set(termId, [[], []]); // index 0: withTPD, index 1: withoutTPD
                    }

                    const groups = groupedSlices.get(termId)!;

                    if (slice.connectedTPD) {
                        groups[0].push(slice);
                    } else {
                        groups[1].push(slice);
                    }
                }

                const flattened: IAdvanceSlice[] = [];

                // Once it's grouped we order by paymentId and sliceRow (in that priority order).
                for (const [termId, [withTPD, withoutTPD]] of groupedSlices) {
                    const sortFn = multiSortWithOrder([
                        ["paymentId", "asc"],
                        ["sliceRow", "asc"],
                    ]);
                    withTPD.sort(sortFn);
                    withoutTPD.sort(sortFn);

                    // Then we flatten the whole thing because it's already ordered and the table needs a simple array.
                    flattened.push(...withTPD, ...withoutTPD);
                }

                state.advanceSlices = flattened;
            }
            state.allAdvancesDatesRange = payload.allAdvancesDatesRange;
            state.advancePaymentsStatus = "succeeded";
        });
        builder.addCase(fetchAllAdvanceSlices.pending, (state) => {
            state.advancePaymentsStatus = "pending";
        });
        builder.addCase(fetchAllAdvanceSlices.rejected, (state, { payload }) => {
            state.advancePaymentsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(checkIfContractAlreadyCreated.pending, () => {});
        builder.addCase(checkIfContractAlreadyCreated.fulfilled, () => {});
        builder.addCase(checkIfContractAlreadyCreated.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(sendAdvancePaymentMailWithStatement.pending, () => {});
        builder.addCase(sendAdvancePaymentMailWithStatement.fulfilled, () => {
            toast.success(`${t("AdvancePayment.SendStatementMailSuccess")}`);
        });
        builder.addCase(sendAdvancePaymentMailWithStatement.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(changeAdvancePaymentStatus.pending, () => {});
        builder.addCase(changeAdvancePaymentStatus.fulfilled, () => {
            toast.success(`${t("AdvancePayment.UpdateAdvancePaymentSuccess")}`);
        });
        builder.addCase(changeAdvancePaymentStatus.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(deleteAdvancePayment.pending, () => {});
        builder.addCase(deleteAdvancePayment.fulfilled, () => {
            toast.success(`${t("AdvancePayment.DeleteAdvancePaymentSuccess")}`);
        });
        builder.addCase(deleteAdvancePayment.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(addAdvancePayment.pending, () => {});
        builder.addCase(addAdvancePayment.fulfilled, (state, action) => {
            const newAdvance = action.payload.data;
            const newSlices = newAdvance.slices.map((slice) => {
                return { ...slice, ...newAdvance };
            });
            state.advanceSlices = [...newSlices, ...state.advanceSlices];
            toast.success(`${t("AdvancePayment.AddAdvancePaymentSuccess")}`);
        });
        builder.addCase(addAdvancePayment.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(updateAdvancePayment.fulfilled, (state, { payload }) => {
            state.advanceSlices = state.advanceSlices.map((contract) => {
                if (contract.id === payload.id) {
                    return { ...contract, slices: payload.slices };
                } else {
                    return contract;
                }
            });
        });
        builder.addCase(updateAdvancePayment.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteAdvanceSlice.fulfilled, (state, action) => {
            const { sliceId, deleteWholeAdvance, termId } = action.meta.arg;
            if (deleteWholeAdvance) {
                state.advanceSlices = [...state.advanceSlices].filter((item) => item.termId !== termId);
            } else {
                state.advanceSlices = [...state.advanceSlices].filter((item) => item.id !== sliceId);
            }
        });
        builder.addCase(deleteAdvanceSlice.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(getAdvanceLinkedToTPD.fulfilled, (state, { payload }) => {
            state.advancePayment = payload;
        });
    },
});

export const { setLastSentAtAdvance, updateAdvancePayments, validateAdvanceBankInfos } = advancePaymentSlice.actions;
export default advancePaymentSlice.reducer;
